'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AmeliyatsizYuzGermeYontemleriPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/ameliyatsiz-yuz-germe-yontemleri';
  const pageTitle = 'Ameliyatsız Yüz Germe Yöntemleri';
  const pageDescription = 'Ameliyatsız yüz germe yöntemleri nelerdir? Botoks, dolgu, ip askı, HIFU, mezoterapi gibi modern tekniklerle genç ve sıkı bir cilt mümkün!';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Ameliyatsız Yüz Germe Yöntemleri', href: '/tr/blog/ameliyatsiz-yuz-germe-yontemleri' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Ameliyatsız yüz germe yöntemleri ve modern estetik teknikler değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Non-Surgical Lifting
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ameliyatsız Yüz Germe Yöntemleri
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>5 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Zamanla cilt elastikiyetini kaybeder, yerçekimi etkisiyle sarkmalar ve kırışıklıklar oluşur. Ancak günümüzde cerrahi yüz germe işlemlerine alternatif olarak birçok ameliyatsız yüz gençleştirme yöntemi geliştirilmiştir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yöntemler, cerrahi kesi olmadan, doğal mimikleri koruyarak cildi toparlar, sıkılaştırır ve genç bir görünüm kazandırır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-8">
                <p className="text-blue-800 font-medium">
                  💡 Amaç: Yüz hatlarını koruyarak doğal bir tazelik sağlamak, "donuk" değil "dinamik" bir gençleşme elde etmektir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Botoks ile Yüz Germe
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, yüz kaslarının kasılmasını geçici olarak azaltarak kırışıklıkları giderir ve lifting etkisi oluşturur. Özellikle alın, kaş arası, göz çevresi ve çene hattında yüzü daha gergin ve dinlenmiş gösterir.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Mimik çizgilerini yumuşatır</li>
                  <li>✅ Alın ve göz çevresinde pürüzsüzlük sağlar</li>
                  <li>✅ Etkisi 4–6 ay sürer</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Dolgu ile Yüz Kontürü Şekillendirme
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hyaluronik asit dolguları, cilt altına hacim kazandırarak yüzün doğal hatlarını geri kazandırır. Elmacık kemiği, nazolabial çizgi ve çene hattında uygulanır.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-purple-800 space-y-2">
                  <li>✅ Cilt altı desteği sağlar</li>
                  <li>✅ Yüzü yukarı taşır</li>
                  <li>✅ 12–18 ay kalıcı etki</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. İp Askı (Thread Lift)
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                İp askı yöntemi, özel eriyebilen iplerin deri altına yerleştirilmesiyle cildi yukarı asma işlemidir. Cilt dokusunu anında yukarı çekerken, kolajen üretimini de uyarır.
              </p>

              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>✅ Hemen fark edilir "lifting" etkisi</li>
                  <li>✅ 1–1,5 yıl kalıcılık</li>
                  <li>✅ Doğal ve dengeli yüz hatları</li>
                </ul>
                <p className="text-orange-800 mt-3 font-medium">
                  💡 En sık uygulanan bölgeler: yanak, çene hattı, kaş kaldırma, boyun bölgesi.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. HIFU (Odaklı Ultrason)
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                High Intensity Focused Ultrasound (HIFU) teknolojisi, cilt altı dokulara ultrason enerjisi göndererek kolajen üretimini artırır ve cildi sıkılaştırır.
              </p>

              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1 ay içinde başlar, 3 ayda maksimum düzeye ulaşır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılığı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Avantajı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi olmadan SMAS tabakasında sıkılaşma sağlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-teal-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-teal-800 space-y-2">
                  <li>✅ Cilt altı destek tabakası güçlenir</li>
                  <li>✅ Sarkmalar toparlanır</li>
                  <li>✅ Anestezi gerekmez</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Mezoterapi ve PRP
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Cilt altına vitamin, aminoasit, hyaluronik asit veya PRP (Platelet Rich Plasma) enjekte edilerek cilt yenilenmesi sağlanır.
              </p>

              <div className="bg-pink-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-pink-800 space-y-2">
                  <li>✅ Cilt kalitesi artar</li>
                  <li>✅ Nem dengesi sağlanır</li>
                  <li>✅ İnce çizgiler azalır</li>
                </ul>
                <p className="text-pink-800 mt-3 font-medium">
                  Genellikle diğer yüz germe yöntemleriyle kombine edilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kombine Uygulamalar
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Modern estetikte tek yöntem yerine çoklu kombinasyonlar tercih edilir. Örneğin:
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-indigo-800 space-y-2">
                  <li><strong>Botoks + Dolgu</strong> → Hem lifting hem kırışıklık giderme</li>
                  <li><strong>İp Askı + HIFU</strong> → Derin ve yüzeysel sıkılaşma</li>
                  <li><strong>Mezoterapi + PRP</strong> → Cilt kalitesi artışı</li>
                </ul>
                <p className="text-indigo-800 mt-3 font-medium">
                  💡 Bu kombinasyonlar kişiye özel planlanır ve doğal sonuçlar verir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Yöntem Karşılaştırma Tablosu
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Yöntem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Süresi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Kalıcılık
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Avantaj
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Botoks</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 gün içinde başlar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–6 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Mimik çizgilerini azaltır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dolgu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hemen görünür</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim ve lifting sağlar</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İp Askı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Anında etki</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi olmadan yüz kaldırma</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">HIFU</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1 ayda başlar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–1.5 yıl</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Derin doku sıkılaştırma</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Mezoterapi / PRP</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–3 seans sonrası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–9 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilt kalitesini artırır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Ameliyatsız yüz germe yöntemleri ve modern estetik teknikler"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Ameliyatsız yüz germe kalıcı mıdır?</h3>
                  <p className="text-gray-700">Hayır, ancak düzenli bakım ve seanslarla etkisi uzun süre korunabilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. En etkili ameliyatsız yüz germe yöntemi hangisidir?</h3>
                  <p className="text-gray-700">Kişiye göre değişir. Kombine yöntemler (örneğin ip askı + HIFU) en güçlü sonucu verir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. İşlem sonrası iz kalır mı?</h3>
                  <p className="text-gray-700">Hayır, hiçbirinde cerrahi kesi yapılmadığı için iz oluşmaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Yüz ifadesi doğal kalır mı?</h3>
                  <p className="text-gray-700">Evet, doğru doz ve teknikle uygulandığında mimikler korunur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Kaç yaşında başlanmalı?</h3>
                  <p className="text-gray-700">Genellikle 30'lu yaşlarda koruyucu amaçla başlanabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – "Non-Surgical Facial Rejuvenation"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Ameliyatsız Yüz Germe Uygulamaları Kılavuzu</li>
                <li>Mayo Clinic – "Ultrasound and Thread Lifting Techniques"</li>
                <li>Harvard Health Publishing – "Combining Botox, Fillers and HIFU Safely"</li>
                <li>ISAPS Global Report 2024 – "Trends in Non-Invasive Facial Lifting"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#yüzgerme</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#ameliyatsızyüzgerme</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#hifu</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#ipaskı</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#mezoterapi</span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">#prp</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Ameliyatsız Yüz Germe İçin Randevu Alın</h3>
              <p className="mb-6">Modern estetik tekniklerle genç ve sıkı bir cilt için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Ameliyatsız yüz germe hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-teal-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/6-nokta-lifting-nedir-yuz-genclestirmede-yeni-nesil-teknik"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">6 Nokta Lifting Nedir?</h4>
              <p className="text-gray-600 text-sm">Yüz gençleştirmede yeni nesil teknik.</p>
            </Link>
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Cerrahi olmayan yüz gençleştirmenin bilimsel açıklaması.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
